import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Plus, Building2, Search } from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';
import { adminApi, type OrgSummary } from '@/lib/api/admin';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export const Route = createFileRoute('/admin/orgs/')({
  component: OrgListPage,
});

function OrgListPage() {
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    adminApi
      .listOrgs()
      .then(setOrgs)
      .finally(() => setLoading(false));
  }, []);

  const filtered = orgs.filter(
    (o) =>
      o.name.toLowerCase().includes(search.toLowerCase()) ||
      o.slug.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-heading font-bold text-foreground shrink-0">Organizations</h1>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input
              type="search"
              placeholder="Search organizations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search organizations"
              className="pl-9 w-56"
            />
          </div>
          <Button asChild className="bg-orange-500 text-white hover:bg-orange-600 focus-visible:ring-orange-500">
            <Link to="/admin/orgs/new">
              <Plus className="h-4 w-4" aria-hidden="true" />
              New Organization
            </Link>
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Name
              </TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Batches
              </TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Members
              </TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Pending
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton className="h-4 w-48" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-10" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-10" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-10" />
                  </TableCell>
                </TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={4}>
                  <EmptyState
                    icon={Building2}
                    title="No organizations yet"
                    description="Create your first organization to get started."
                    className="py-12"
                  />
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((o) => (
                <TableRow key={o.id}>
                  <TableCell>
                    <Link
                      to="/admin/orgs/$orgId"
                      params={{ orgId: o.id }}
                      className="text-sm font-medium text-foreground hover:text-primary"
                    >
                      {o.name}
                    </Link>
                  </TableCell>
                  <TableCell className="text-sm tabular-nums">
                    <Link
                      to="/admin/batches"
                      search={{ org: o.id, tab: 'active' }}
                      className="text-foreground hover:text-primary hover:underline"
                    >
                      {o.batch_count ?? 0}
                    </Link>
                  </TableCell>
                  <TableCell className="text-sm tabular-nums text-foreground">
                    {o.member_count}
                  </TableCell>
                  <TableCell>
                    {o.pending_count > 0 ? (
                      <Badge
                        variant="outline"
                        className="border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-400"
                      >
                        {o.pending_count}
                      </Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
